import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import PersonalDetails from "./PersonalDetails";
import FamilyDetails from "./FamilyDetails";
import OtherDetails from "./OtherDetails";

import coupleImg from "../../Assets/review/db2.jpg";
import heading from "../../Assets/SignUp/heading.png";
import bg from "../../Assets/SignUp/bg.png";
import absolute from "../../Assets/SignUp/absolute.png";
import { useDispatch } from "react-redux";
import { userImagesUpload, userProfileDetails } from "../../Redux/Actions/AuthAction";
import { toast } from "react-toastify";
import useTitle from "../../Hooks/useTitle";

const UserProfile = () => {
  // Set page title using custom hook
  useTitle('Profile');
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // State to track current step in multi-step form
  const [currentStep, setCurrentStep] = useState(1);

  // State to track if images have been uploaded to prevent duplicate uploads
  const [imagesUploaded, setImagesUploaded] = useState(false);

  // Effect to check authentication and image upload status on component mount
  useEffect(() => {
    // Check if user has registration token (is authenticated)
    const regToken = localStorage.getItem('regToken');
    if (!regToken) {
      navigate('/signup'); // Redirect to signup if not authenticated
    }
    
    // Check if images were already uploaded in current session
    const uploadedFlag = sessionStorage.getItem('imagesUploaded');
    if (uploadedFlag === 'true') {
      setImagesUploaded(true); // Mark images as uploaded
    }
  }, [navigate]);

  // Centralized state for all form data across all steps
  const [formData, setFormData] = useState({
    // Personal Details Section
    gender: "",
    date_of_birth: "", // Will be formatted as YYYY-MM-DD
    religion: "",
    cast: "",
    height: "", // Will be formatted as "ft.in" (e.g., "5.8")
    weight: "",
    country_code: "",
    phone_number: "",
    city: "",
    country: "",
    years_in_country: "",
    visa_status: "",
    nationality: "",
    diet: "",
    marital_status: "",
    live_with_family: "", // Boolean value as string
    hobbies: "",
    extra_skills: "",
    educations: [], // Array of education objects
    highschools: [], // Array of highschool objects
    rewards: [], // Array of reward objects
    awards: [], // Array of award objects
    medals: [], // Array of medal objects
    certifications: [], // Array of certification objects
    occupations: [], // Array of occupation objects
    
    // Family Details Section
    father_name: "",
    father_occupation: "",
    father_phone_number: "",
    father_country_code: "",
    mother_name: "",
    mother_occupation: "",
    mother_phone_number: "",
    mother_country_code: "",
    parents_native: "",
    parents_cast: "",
    maternal_uncle_name: "",
    maternal_uncle_occupation: "",
    maternal_phone_number: "",
    maternal_uncle_native_place: "",
    
    // Other Details Section
    partners_parents_origin_choice: "",
    sender_image: "", // Base64 encoded image string
    expected_education: "",
    senders_name: "",
    senders_country_code: "",
    senders_phone_number: "",
    person_to_contact_name: "",
    person_to_contact_country_code: "",
    person_to_contact_phone: "",
    instagram: "",
    linkedin: "",
    snapchat: "",
    twitter: "",
    facebook: ""
  });

  // State for Date of Birth (split into day, month, year for better UX)
  const [DOB, setDOB] = useState({
    dobDay: "",    // Day (1-31)
    dobMonth: "",  // Month (1-12) - selected from dropdown
    dobYear: ""    // Year (e.g., 1990)
  });

  // State for user height (split into feet and inches for better UX)
  const [userHeight, setUserHeight] = useState({
    heightFt: "", // Feet part of height
    heightIn: ""  // Inches part of height
  });

  // Handler for height input changes
  const handleUserHeightChange = (e) => {
    const { name, value } = e.target;
    setUserHeight((prev) => ({ ...prev, [name]: value }));
  };

  // Handler for date of birth input changes
  const handleUserDOBChange = (e) => {
    const { name, value } = e.target;
    setDOB((prev) => ({ ...prev, [name]: value }));
  };

  // State for user images (4 image slots)
  const [images, setImages] = useState([null, null, null, null]);
  
  // State for education list (dynamic form fields)
  const [educationList, setEducationList] = useState([
    { degree: "", from_year: "", to_year: "", university_name: "", city: "" },
  ]);
  
  // State for occupation list (dynamic form fields)
  const [occupationList, setOccupationList] = useState([
    { role: "", from_year: "", to_year: "", company_name: "" },
  ]);
  
  // State for awards list (dynamic form fields)
  const [awardsList, setAwardsList] = useState([
    { title: "", year: "" }
  ]);
  
  // State for rewards list (dynamic form fields)
  const [rewardsList, setRewardsList] = useState([
    { title: "", year: "" }
  ]);
  
  // State for medals list (dynamic form fields)
  const [medalsList, setMedalsList] = useState([
    { title: "", year: "" }
  ]);
  
  // State for certifications list (dynamic form fields)
  const [certificationsList, setCertificationsList] = useState([
    { title: "", year: "" }
  ]);
  
  // State for highschool list (dynamic form fields)
  const [highschoolList, setHighschoolList] = useState([
    { school_name: "", from_year: "", to_year: "", city: "" }
  ]);

  // Handler for phone number changes
  const handlePhoneNumberChange = (field, newPhone) => {
    setFormData((prev) => ({ ...prev, [field]: newPhone }));
  };

  // Handler for country code changes
  const handleCodeChange = (field, newCode) => {
    setFormData((prev) => ({ ...prev, [field]: newCode }));
  };

  // Handler for country changes
  const handleCountryChange = (field, newCountry) => {
    setFormData((prev) => ({ ...prev, [field]: newCountry }));
  };

  // Generic handler for form data updates
  const updateFormData = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Navigation handler for moving to next step
  const nextStep = () => {
    // When moving from step 1 to step 2, consolidate all dynamic list data
    if (currentStep === 1) {
      setFormData((prev) => ({
        ...prev,
        // Combine all dynamic lists into formData for submission
        educations: educationList,
        highschools: highschoolList,
        occupations: occupationList,
        rewards: rewardsList,
        awards: awardsList,
        medals: medalsList,
        certifications: certificationsList
      }));
    }
    setCurrentStep((prev) => prev + 1); // Move to next step
  };

  // Navigation handler for moving to previous step
  const prevStep = () => {
    setCurrentStep((prev) => prev - 1); // Move to previous step
  };

  // Helper function to format image upload errors for user display
  const formatImageError = (errorObj) => {
    if (!errorObj) return null;
    const firstField = Object.keys(errorObj)[0]; // e.g., "images.1"
    if (!firstField) return null;

    // Extract image index (0-based) and convert to human-readable (1-based)
    const match = firstField.match(/images\.(\d+)/);
    const index = match ? parseInt(match[1], 10) + 1 : null;

    const message = errorObj[firstField][0] || "Invalid image";

    // Return user-friendly error message
    return index
      ? `Image ${index}: Please upload a file in jpeg, jpg, png, or gif format.`
      : `Please upload a file in jpeg, jpg, png, or gif format.`;
  };

  // Main form submission handler
  const handleSubmit = async () => {
    const payload = {};
    console.log('formData', formData); // Debug log

    // COMMENTED OUT: Required fields validation
    /*
    // Define required fields for validation
    const requiredFields = [
      // Personal Details - Required Fields
      'gender', 'date_of_birth', 'religion', 'cast', 'height', 'weight',
      'country_code', 'phone_number', 'city', 'country', 'nationality',
      'diet', 'marital_status', 'live_with_family',

      // Family Details - Required Fields
      'father_name', 'father_occupation', 'father_phone_number', 'father_country_code',
      'mother_name',
      'parents_native', 'maternal_uncle_name', 'maternal_uncle_native_place',
    ];

    // Check for empty required fields
    const emptyRequiredFields = requiredFields.filter(field => {
      const value = formData[field];
      return value === "" || value === null || value === undefined;
    });

    // Show error if any required fields are empty
    if (emptyRequiredFields.length > 0) {
      console.log('emptyRequiredFields', emptyRequiredFields);
      return toast.error(`Please fill all required fields !!`);
    }
    */

    // Validate images (only if not already uploaded)
    if (!imagesUploaded && images.includes(null)) {
      return toast.error("Please add all 4 images!");
    }

    // Validate education data
    const incompleteEducation = educationList.some(edu => !edu.degree);
    if (incompleteEducation) {
      return toast.error("Degree is required.");
    }

    // Validate occupation data
    const incompleteOccupation = occupationList.some(edu => !edu.role);
    if (incompleteOccupation) {
      return toast.error("Please fill the Role field in occupation.");
    }

    // Validate highschool data (all or nothing for each entry)
    const incompleteHighschools = highschoolList.some(hs => {
      const { school_name, from_year, to_year, city } = hs;
      // If any field is filled, all fields must be filled
      const anyFilled = school_name || from_year || to_year || city;
      const allFilled = school_name && from_year && to_year && city;
      return anyFilled && !allFilled;
    });

    if (incompleteHighschools) {
      return toast.error("Please fill all field of highschool.");
    }

    // Validate rewards data (both title and year required if either is filled)
    const incompleteRewards = rewardsList.some(reward => {
      const { title, year } = reward;
      const anyFilled = title || year;
      const allFilled = title && year;
      return anyFilled && !allFilled;
    });

    if (incompleteRewards) {
      return toast.error("If any reward is filled, both Title and Year are required.");
    }

    // Validate awards data
    const incompleteAwards = awardsList.some(award => {
      const { title, year } = award;
      const anyFilled = title || year;
      const allFilled = title && year;
      return anyFilled && !allFilled;
    });
    if (incompleteAwards) {
      return toast.error("If any award is filled, both Title and Year are required.");
    }

    // Validate medals data
    const incompleteMedals = medalsList.some(medal => {
      const { title, year } = medal;
      const anyFilled = title || year;
      const allFilled = title && year;
      return anyFilled && !allFilled;
    });
    if (incompleteMedals) {
      return toast.error("If any medal is filled, both Title and Year are required.");
    }

    // Validate certifications data
    const incompleteCertifications = certificationsList.some(cert => {
      const { title, year } = cert;
      const anyFilled = title || year;
      const allFilled = title && year;
      return anyFilled && !allFilled;
    });
    if (incompleteCertifications) {
      return toast.error("If any certification is filled, both Title and Year are required.");
    }

    // Helper function to sanitize form values
    const sanitizeValue = (val) => {
      if (Array.isArray(val)) return val;
      const trimmed = val?.trim();
      return trimmed ? trimmed : undefined;
    };

    // Process form data and build payload
    Object.keys(formData).forEach((key) => {
      // Skip array fields (they are processed separately)
      if (key === "educations" || key === "occupations" || key === "rewards" || key === "awards"
        || key === "medals" || key === "certifications" || key === "highschools"
      ) return;

      let value = formData[key];

      // Sanitize non-array values
      if (!Array.isArray(value)) {
        value = sanitizeValue(value || "");
      }

      // Convert height and weight to numbers
      if ((key === "height" || key === "weight") && value !== undefined) {
        value = parseFloat(value);
      }

      // Handle live_with_family boolean conversion
      if (key === "live_with_family") {
        // Convert various boolean representations to actual boolean
        if (formData.live_with_family === true || formData.live_with_family === "true" || formData.live_with_family === "Yes") {
          value = true;
        } else if (formData.live_with_family === false || formData.live_with_family === "false" || formData.live_with_family === "No") {
          value = false;
        } else {
          value = undefined;
        }
      }

      // Add to payload if value is defined
      if (value !== undefined) {
        payload[key] = value;
      }
    });

    // Process highschools array (filter empty entries and convert years to numbers)
    if (Array.isArray(formData.highschools)) {
      const highArr = formData.highschools
        .filter((h) => h.school_name && h.school_name.trim() !== "")
        .map((h) => ({
          school_name: h.school_name,
          from_year: Number(h.from_year) || "",
          to_year: Number(h.to_year) || "",
          city: h.city || "",
        }));
      if (highArr.length > 0) payload.highschools = highArr;
    }

    // Process rewards array
    if (Array.isArray(formData.rewards)) {
      const rewArr = formData.rewards
        .filter((rew) => rew.title.trim() !== "")
        .map((rew) => ({
          title: rew.title,
          year: Number(rew.year),
        }));
      if (rewArr.length > 0) payload.rewards = rewArr;
    }

    // Process awards array
    if (Array.isArray(formData.awards)) {
      const awaArr = formData.awards
        .filter((awa) => awa.title.trim() !== "")
        .map((awa) => ({
          title: awa.title,
          year: Number(awa.year),
        }));
      if (awaArr.length > 0) payload.awards = awaArr;
    }

    // Process medals array
    if (Array.isArray(formData.medals)) {
      const medArr = formData.medals
        .filter((med) => med.title.trim() !== "")
        .map((med) => ({
          title: med.title,
          year: Number(med.year),
        }));
      if (medArr.length > 0) payload.medals = medArr;
    }

    // Process certifications array
    if (Array.isArray(formData.certifications)) {
      const cerArr = formData.certifications
        .filter((cer) => cer.title.trim() !== "")
        .map((cer) => ({
          title: cer.title,
          year: Number(cer.year),
        }));
      if (cerArr.length > 0) payload.certifications = cerArr;
    }

    // Process educations array
    if (Array.isArray(formData.educations)) {
      const eduArr = formData.educations
        .filter((edu) => edu.degree.trim() !== "")
        .map((edu) => ({
          degree: edu.degree,
          from_year: edu.from_year ? Number(edu.from_year) : "",
          to_year: edu.to_year ? Number(edu.to_year) : "",
          university_name: edu.university_name,
          city: edu.city,
          college: edu.college,
        }));
      if (eduArr.length > 0) payload.educations = eduArr;
    }

    // Process occupations array
    if (Array.isArray(formData.occupations)) {
      const occArr = formData.occupations
        .filter((occ) => occ.role.trim() !== "")
        .map((occ) => ({
          role: occ.role,
          from_year: occ.from_year ? Number(occ.from_year) : "",
          to_year: occ.to_year ? Number(occ.to_year) : "",
          company_name: occ.company_name,
        }));
      if (occArr.length > 0) payload.occupations = occArr;
    }

    console.log("Final Payload:", payload); // Debug log

    // Upload images only if they haven't been uploaded yet
    if (!imagesUploaded) {
      try {
        const imageData = new FormData();
        images.forEach((file, index) => {
          imageData.append(`images[]`, file); // Append each image to FormData
        });
        const res1 = await dispatch(userImagesUpload(imageData));

        if (!res1) {
          return console.log('error while image upload');
        }

        // Mark images as uploaded to prevent duplicate uploads
        setImagesUploaded(true);
        sessionStorage.setItem('imagesUploaded', 'true');

      } catch (error) {
        console.log('error while image upload: ', error);
        // Format and display image upload errors
        if (error.response?.data?.errors) {
          const formattedMsg = formatImageError(error?.response?.data?.errors);
          if (formattedMsg) return toast.error(formattedMsg);
        }
        return toast.error(error.response?.data?.message || "Something went wrong while uploading images.");
      }
    }

    // Submit profile data to API
    try {
      const res = await dispatch(userProfileDetails(payload));
      if (!res) {
        return console.log('error while profile details submition: ');
      }
      
      // Clean up session storage after successful submission
      sessionStorage.removeItem('imagesUploaded');

      toast.success('Your registration is complete! Please log in to continue.');
      navigate('/signin'); // Redirect to login page
    } catch (error) {
      console.log('error while profile details submition: ', error);
      return toast.error(error.response.data.message);
    }
  };

  // Render appropriate step component based on current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalDetails
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            images={images}
            setImages={setImages}
            highschoolList={highschoolList}
            setHighschoolList={setHighschoolList}
            awardsList={awardsList}
            setAwardsList={setAwardsList}
            rewardsList={rewardsList}
            setRewardsList={setRewardsList}
            medalsList={medalsList}
            setMedalsList={setMedalsList}
            certificationsList={certificationsList}
            setCertificationsList={setCertificationsList}
            educationList={educationList}
            setEducationList={setEducationList}
            occupationList={occupationList}
            setOccupationList={setOccupationList}
            handlePhoneNumberChange={handlePhoneNumberChange}
            handleCodeChange={handleCodeChange}
            handleCountryChange={handleCountryChange}
            DOB={DOB}
            handleUserDOBChange={handleUserDOBChange}
            userHeight={userHeight}
            handleUserHeightChange={handleUserHeightChange}
            setDOB={setDOB} // Pass setDOB function for month dropdown
          />
        );
      case 2:
        return (
          <FamilyDetails
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
            handlePhoneNumberChange={handlePhoneNumberChange}
            handleCodeChange={handleCodeChange}
            handleCountryChange={handleCountryChange}
          />
        );
      case 3:
        return (
          <OtherDetails
            formData={formData}
            updateFormData={updateFormData}
            prevStep={prevStep}
            onSubmit={handleSubmit}
            handlePhoneNumberChange={handlePhoneNumberChange}
            handleCodeChange={handleCodeChange}
            handleCountryChange={handleCountryChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 h-screen overflow-hidden">
      {/* Left Image Section - Hidden on mobile, visible on large screens */}
      <div className="hidden lg:block relative flex justify-center items-center p-4 lg:h-full lg:overflow-hidden">
        <div className="w-full lg:h-full relative">
          <img
            src={coupleImg}
            alt="Couple"
            className="w-full lg:h-full object-cover rounded-xl"
          />
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-center text-white">
            <img src={absolute} alt="Decorative Icon" className="mx-auto mb-2" />
            <h1 className="text-2xl font-bold">Add USA</h1>
            <h2 className="text-3xl font-bold">Gujarati Community</h2>
          </div>
        </div>
      </div>
      
      {/* Right Form Section */}
      <div
        className="flex flex-col items-center justify-center h-screen w-full p-0 lg:p-16"
        style={{
          backgroundImage: `url(${bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex justify-center">
          <img src={heading} alt="Matrimony" className="h-12 w-32" />
        </div>
        <h2 className="text-center text-xl font-semibold my-2">
          {currentStep === 1
            ? "Personal Details"
            : currentStep === 2
              ? "Family Details"
              : "Other Details"}
        </h2>
        {renderStep()}
      </div>
    </div>
  );
};

export default UserProfile;